import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import { makeT } from "@/lib/workerI18n";
import { uploadPhotos } from "@/lib/photoUpload";
import { PhotoAnnotator } from "@/components/PhotoAnnotator";
import { ShieldCheck, MapPin, Check, X, Loader2, Camera, Image as ImageIcon, Send, AlertTriangle, PartyPopper, HardHat, Globe, ArrowLeft, ArrowRight, ExternalLink, Sparkles, Trash2, Download, ChevronRight, ChevronUp } from "lucide-react";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { WorkerSaveModal } from "@/components/WorkerSaveModal";
import { InstallPrompt } from "@/components/InstallPrompt";
import { useAuth } from "@/lib/auth";
import { analytics } from "@heycatch/sdk";

const NAME_KEY = "sp-share-actor-name";
const DEMO_TOKENS = new Set(["ShZOCnasWqst", "CurbPourDemo"]);
const thumbOf = (p) => p?.thumb_url || p?.url || imgUrl(p.thumbnail_cloud_path || p.cloud_path, 800, 80);
const fullOf = (p) => p?.url || imgUrl(p.cloud_path, 1600, 90);

/* Photo that recovers from an expired/stale link: on failure it falls back to
   the /api/img proxy (re-signs R2 server-side), then shows a placeholder. */
function PhotoImg({ photo, onOpen, dim }) {
  const base = fullOf(photo);
  const proxy = photo && photo.cloud_path
    ? imgUrl(photo.cloud_path, 1600, 90)
    : null;
  const [url, setUrl] = useState(base);
  const [strike, setStrike] = useState(0);
  useEffect(() => { setUrl(base); setStrike(0); }, [base]);

  if (strike >= 2) {
    return (
      <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-1 bg-neutral-900 px-6 text-center">
        <AlertTriangle className="h-6 w-6 text-amber-400" />
        <p className="text-xs font-medium text-neutral-400">Photo unavailable — ask the sender to re-upload. You can still mark this done from the description below.</p>
      </div>
    );
  }
  return (
    <button onClick={onOpen} className="block w-full" data-testid={`public-photo-${photo.id}`}>
      <img
        src={url}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => { if (proxy && url !== proxy) { setUrl(proxy); setStrike(1); } else { setStrike(2); } }}
        className={`max-h-[42vh] w-full bg-neutral-900 object-cover transition-opacity duration-200 ${dim ? "opacity-60" : "opacity-100"}`}
        style={{ aspectRatio: "4 / 3" }}
      />
    </button>
  );
}

function ItemCard({ item, readOnly, pending, onToggle, onStatus, onTakeProof, onDeleteProof, proofUploading, uploadFailed, onOpenPhoto, index, tr, t }) {
  const ref = (item.photos || []).filter((p) => p.kind !== "proof");
  const proof = (item.photos || []).filter((p) => p.kind === "proof");
  const hero = ref[0];
  const extraRef = ref.slice(1);
  const isDone = item.status === "done" || item.status === "sub_done";
  const isIssue = item.status === "issue";
  const isNa = item.status === "na";
  const inFlight = !!pending;
  const [note, setNote] = useState(item.description || "");
  useEffect(() => { setNote(item.description || ""); }, [item.description]);
  // Swipeable gallery for this item: every reference photo, then every proof photo.
  const gallery = [
    ...ref.map((p) => ({ url: fullOf(p), label: "Reference" })),
    ...proof.map((p) => ({ url: fullOf(p), label: "Proof" })),
  ];
  const openFromUrl = (url) => onOpenPhoto({ photos: gallery, index: Math.max(0, gallery.findIndex((g) => g.url === url)) });
  const title = (tr?.title) || item.title || `${t("item")} ${index + 1}`;
  const [open, setOpen] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const collapsed = (isDone || readOnly) && !open;

  // #5 status chip — plain words instead of a bare checkbox
  const chip = isIssue
    ? { c: "rose", icon: AlertTriangle, label: t("issueFlagged") }
    : isNa
    ? null
    : isDone
    ? { c: "emerald", icon: Check, label: t("complete") }
    : readOnly
    ? null
    : { c: "amber", icon: AlertTriangle, label: t("photoNeeded") };
  const chipCls = { emerald: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300", amber: "border-amber-500/40 bg-amber-500/10 text-amber-300", rose: "border-rose-500/40 bg-rose-500/15 text-rose-300" };

  // #8 collapse completed / already-sent items into a compact line
  if (collapsed) {
    const th = proof[0] || hero;
    return (
      <div className={`flex items-center gap-3 rounded-xl border px-3.5 py-3 ${isDone ? "border-emerald-500/30 bg-emerald-500/[0.07]" : "border-neutral-800 bg-[#141414]"}`} data-testid={`public-item-${item.id}`}>
        <button type="button" disabled={readOnly} data-testid={`public-complete-${item.id}`} onClick={() => !readOnly && onToggle(item)} className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${isDone ? "border-emerald-500 bg-emerald-500" : "border-neutral-600"}`}>
          {inFlight ? <span className="h-3 w-3 animate-pulse rounded-full bg-emerald-400" /> : isDone ? <Check className="h-5 w-5 text-black" strokeWidth={3.5} /> : null}
        </button>
        <button type="button" onClick={() => setOpen(true)} data-testid={`public-expand-${item.id}`} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <span className="min-w-0 flex-1">
            <span className="block text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-400/70">{t("item")} {index + 1}</span>
            <span className={`block truncate text-[15px] font-medium ${isDone ? "text-neutral-400 line-through" : "text-neutral-100"}`}>{title}</span>
          </span>
          {th && <img src={thumbOf(th)} alt="" loading="lazy" className="h-10 w-10 shrink-0 rounded-lg object-cover ring-1 ring-emerald-500/30" />}
          <ChevronRight className="h-5 w-5 shrink-0 text-neutral-500" />
        </button>
      </div>
    );
  }

  const border = isDone
    ? "border-emerald-500/30 bg-emerald-500/[0.06]"
    : isIssue
    ? "border-rose-500/40 bg-rose-500/5"
    : isNa
    ? "border-neutral-800 bg-neutral-900/60 opacity-80"
    : "border-neutral-700 bg-[#171717]";

  return (
    <div className={`overflow-hidden rounded-2xl border shadow-[0_6px_24px_-6px_rgba(0,0,0,0.7)] ring-1 ring-white/[0.05] ${border}`} data-testid={`public-item-${item.id}`}>
      {hero && (
        <div className="relative">
          <PhotoImg photo={hero} onOpen={() => openFromUrl(fullOf(hero))} dim={isDone} />
          {isDone && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="rotate-[-8deg] rounded-sm border-4 border-emerald-400/90 bg-black/40 px-4 py-1 backdrop-blur-sm" data-testid={`public-stamp-${item.id}`}>
                <span className="text-xl font-black tracking-[0.3em] text-emerald-300">COMPLETE</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Header: title + status chip (no bare checkbox) */}
      <div className="flex items-start gap-3 p-4 pb-3">
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-400/80">{t("item")} {index + 1}</span>
          <span className={`mt-0.5 block text-[17px] font-medium leading-[1.35] ${isDone ? "text-neutral-500 line-through" : "text-neutral-50"}`}>{title}</span>
          {(tr?.notes || item.notes) && <span className="mt-1 block text-sm leading-snug text-neutral-400">{tr?.notes || item.notes}</span>}
          {readOnly && isIssue && item.description && (
            <span className="mt-2 block rounded-lg bg-rose-500/10 px-2.5 py-1.5 text-sm leading-snug text-rose-300" data-testid={`worker-issue-note-${item.id}`}>⚠ {item.description}</span>
          )}
        </span>
        <div className="flex shrink-0 items-center gap-1.5">
          {chip && (
            <span className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${chipCls[chip.c]}`} data-testid={`status-chip-${item.id}`}>
              <chip.icon className="h-3 w-3" strokeWidth={3} /> {chip.label}
            </span>
          )}
          {(isDone || readOnly) && (
            <button type="button" onClick={() => setOpen(false)} aria-label="Collapse" data-testid={`public-collapse-${item.id}`} className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-500 active:bg-white/[0.06]"><ChevronUp className="h-4 w-4" /></button>
          )}
        </div>
      </div>

      <div className="space-y-3 px-4 pb-4">
        {(proof.length > 0 || extraRef.length > 0) && (
          <div className="grid grid-cols-4 gap-2">
            {extraRef.map((p) => (
              <button key={p.id} onClick={() => openFromUrl(fullOf(p))} className="aspect-square overflow-hidden rounded-lg border border-neutral-800" data-testid={`public-photo-${p.id}`}>
                <img src={thumbOf(p)} alt="" loading="lazy" className="h-full w-full object-cover" />
              </button>
            ))}
            {proof.map((p) => (
              <div key={p.id} className="relative aspect-square">
                <button onClick={() => openFromUrl(fullOf(p))} className="h-full w-full overflow-hidden rounded-lg border border-emerald-500/50" data-testid={`public-proof-${p.id}`}>
                  <img src={thumbOf(p)} alt="" loading="lazy" className="h-full w-full object-cover" />
                </button>
                {!readOnly && (
                  <button type="button" data-testid={`delete-proof-${p.id}`} aria-label={t("removePhoto")} onClick={(e) => { e.stopPropagation(); if (window.confirm(t("removeConfirm"))) onDeleteProof(item.id, p.id); }} className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-rose-500 text-white shadow-md transition-transform active:scale-90">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {!readOnly && !isNa && (
          <>
            {/* #4 Primary action: single photo box (tap → camera or gallery) */}
            {proofUploading ? (
              <div data-testid={`take-proof-${item.id}`} className="flex w-full flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-emerald-500/40 bg-emerald-500/5 py-7 text-emerald-400">
                <Loader2 className="h-6 w-6 animate-spin" strokeWidth={2.5} />
                <span className="text-[14px] font-bold">{t("uploading")}</span>
              </div>
            ) : (
              <button type="button" data-testid={`take-proof-${item.id}`} onClick={() => setPickOpen(true)} className="flex w-full flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-emerald-500/50 bg-emerald-500/[0.06] py-7 text-emerald-400 transition-all active:scale-[0.98] active:bg-emerald-500/10">
                <Camera className="h-7 w-7" strokeWidth={2} />
                <span className="text-[15px] font-bold text-emerald-300">{proof.length > 0 ? t("addAnother") : t("takeProof")}</span>
                <span className="text-[11px] font-medium text-emerald-500/70">{t("photoHint")}</span>
              </button>
            )}
            {uploadFailed && !proofUploading && (
              <p className="flex items-center gap-1.5 text-[13px] font-semibold text-rose-300" data-testid={`proof-upload-failed-${item.id}`}>
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {t("uploadFailedItem")}
              </p>
            )}
            {/* #4 Tertiary: flag an issue (quiet) / issue editor when flagged */}
            {!isIssue ? (
              <button type="button" data-testid={`public-status-issue-${item.id}`} onClick={() => onStatus(item, "issue")} className="flex w-full items-center justify-center gap-1.5 py-1 text-[13px] font-semibold text-neutral-500 transition-colors active:text-rose-300">
                <AlertTriangle className="h-3.5 w-3.5" /> {t("flagIssue")}
              </button>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between rounded-lg border border-rose-500 bg-rose-500/15 px-3 py-2">
                  <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-rose-300"><AlertTriangle className="h-3.5 w-3.5" /> {t("issueFlagged")}</span>
                  <button type="button" data-testid={`public-status-issue-clear-${item.id}`} onClick={() => onStatus(item, "pending")} aria-label="Clear issue" className="flex h-7 w-7 items-center justify-center rounded-full text-rose-300/80 transition-colors active:bg-rose-500/25"><X className="h-4 w-4" /></button>
                </div>
                <textarea data-testid={`issue-note-${item.id}`} value={note} onChange={(e) => setNote(e.target.value)} onBlur={() => onStatus(item, "issue", note)} rows={3} placeholder={t("issuePlaceholder")} className="w-full rounded-lg border border-rose-500/40 bg-rose-500/5 p-3 text-sm leading-snug text-neutral-100 placeholder:text-neutral-500 focus:border-rose-500 focus:outline-none" />
              </div>
            )}
            {/* Undo auto-complete */}
            {isDone && (
              <button type="button" data-testid={`public-complete-${item.id}`} onClick={() => onToggle(item)} className="flex w-full items-center justify-center gap-1.5 py-1 text-[13px] font-semibold text-neutral-500 active:text-neutral-300">
                <X className="h-3.5 w-3.5" /> {t("markNotDone")}
              </button>
            )}
          </>
        )}
      </div>

      {pickOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center" onClick={() => setPickOpen(false)} data-testid={`proof-picker-${item.id}`}>
          <div className="w-full rounded-t-3xl border border-neutral-800 bg-[#171717] p-4 sm:max-w-sm sm:rounded-3xl" onClick={(e) => e.stopPropagation()} style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}>
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-neutral-700 sm:hidden" />
            <label data-testid={`proof-camera-${item.id}`} className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-3.5 text-left transition-colors active:bg-white/[0.06]">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400"><Camera className="h-5 w-5" /></span>
              <span className="min-w-0 flex-1"><span className="block text-[15px] font-semibold text-neutral-100">{t("takePhoto")}</span><span className="block text-xs text-neutral-500">{t("useCamera")}</span></span>
              <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => { onTakeProof(item.id, e.target.files); e.target.value = ""; setPickOpen(false); }} />
            </label>
            <label data-testid={`proof-gallery-${item.id}`} className="flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-3.5 text-left transition-colors active:bg-white/[0.06]">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-neutral-800 text-neutral-300"><ImageIcon className="h-5 w-5" /></span>
              <span className="min-w-0 flex-1"><span className="block text-[15px] font-semibold text-neutral-100">{t("chooseGallery")}</span><span className="block text-xs text-neutral-500">{t("fromPhotos")}</span></span>
              <input type="file" accept="image/*" multiple hidden onChange={(e) => { onTakeProof(item.id, e.target.files); e.target.value = ""; setPickOpen(false); }} />
            </label>
            <button type="button" data-testid={`proof-picker-cancel-${item.id}`} onClick={() => setPickOpen(false)} className="mt-2 h-12 w-full rounded-xl border border-neutral-700 text-[15px] font-semibold text-neutral-300 transition-colors active:bg-white/[0.06]">{t("cancelBtn")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PublicShare() {
  const { token } = useParams();
  const isDemo = DEMO_TOKENS.has(token);
  const navigate = useNavigate();
  const { tryFree } = useAuth();
  const [savingList, setSavingList] = useState(false);
  const [startingOwn, setStartingOwn] = useState(false);
  const isAuthed = (() => { try { return !!localStorage.getItem("sp_token"); } catch { return false; } })();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [pending, setPending] = useState({});
  const [annot, setAnnot] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [showList, setShowList] = useState(false);
  const [actorName, setActorName] = useState(() => { try { return localStorage.getItem(NAME_KEY) || ""; } catch { return ""; } });
  const [message, setMessage] = useState("");
  const [lang, setLang] = useState("");
  const [translating, setTranslating] = useState(false);
  const [tmap, setTmap] = useState({});
  const [bannerDismissed, setBannerDismissed] = useState(() => { try { return !!localStorage.getItem("sp-share-banner-dismissed"); } catch { return false; } });
  const dismissBanner = () => { setBannerDismissed(true); try { localStorage.setItem("sp-share-banner-dismissed", "1"); } catch {} };
  // In-app browsers (Messenger, Instagram, etc.) run in a sealed storage sandbox, so the
  // user's Send-Proof login isn't visible and the report can't auto-attach to their account.
  const inAppBrowser = (() => { try { return /FBAN|FBAV|FB_IAB|Instagram|Messenger|MicroMessenger|Line\/|Twitter|Snapchat|Pinterest|LinkedInApp/i.test(navigator.userAgent || ""); } catch { return false; } })();
  const [iabDismissed, setIabDismissed] = useState(false);
  const openInBrowser = () => {
    const full = window.location.href;
    if (/Android/i.test(navigator.userAgent || "")) {
      window.location.href = `intent://${full.replace(/^https?:\/\//, "")}#Intent;scheme=https;end`;
      return;
    }
    try { navigator.clipboard.writeText(full); toast.success("Link copied — open Safari or Chrome and paste it to save this to your account"); }
    catch { toast.message('Tap the ••• menu and choose "Open in browser"'); }
  };

  useEffect(() => {
    if (!token || isDemo) return;
    let tok; try { tok = localStorage.getItem("sp_token"); } catch { tok = null; }
    if (!tok) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 3 && !cancelled; i++) {
        try { await api.post("/shared/attach", { token }); return; }
        catch { await new Promise((r) => setTimeout(r, 1200)); }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const doTranslate = async (l) => {
    setLang(l);
    if (!l) { setTmap({}); return; }
    setTranslating(true);
    try {
      const { data: tr } = await api.post(`/public/r/${token}/translate`, { lang: l });
      const m = {};
      (tr.items || []).forEach((it) => { m[it.id] = { title: it.title, notes: it.notes }; });
      setTmap(m);
    } catch { toast.error("Couldn't translate — please try again"); setLang(""); }
    finally { setTranslating(false); }
  };

  // Open in the owner-chosen "send" language if set; otherwise auto-detect the worker's phone language (once).
  const autoTried = useRef(false);
  useEffect(() => {
    if (!data || autoTried.current) return;
    autoTried.current = true;
    const forced = data.send_lang;
    if (forced) { doTranslate(forced); return; }
    const supported = ["es", "pt", "ht", "fr", "vi", "zh", "ru", "ko", "pl"];
    const raw = (navigator.language || "").toLowerCase();
    const code = raw.startsWith("zh") ? "zh" : raw.slice(0, 2);
    if (supported.includes(code)) doTranslate(code);
  }, [data]);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    (async () => {
      try { const res = await api.get(`/public/r/${token}`); setData(res.data); }
      catch { setNotFound(true); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const alreadySent = data && ["submitted", "approved", "changes_requested"].includes(data.report.status);
  // Worker page stays editable even after sending so they can fix more items and re-send.
  const readOnly = false;

  const toggleComplete = useCallback(async (item) => {
    if (pending[item.id]) return;
    const next = (item.status === "done" || item.status === "sub_done") ? "pending" : "done";
    setPending((p) => ({ ...p, [item.id]: true }));
    try {
      if (!isDemo) await api.patch(`/public/r/${token}/items/${item.id}/status`, { status: next });
      setData((d) => ({ ...d, items: d.items.map((it) => (it.id === item.id ? { ...it, status: next } : it)) }));
    } catch {
      toast.error("Couldn't save — try again");
    } finally {
      setPending((p) => { const n = { ...p }; delete n[item.id]; return n; });
    }
  }, [pending, token, isDemo]);

  const setStatus = useCallback(async (item, status, note) => {
    const hasNote = note !== undefined;
    setData((d) => ({ ...d, items: d.items.map((it) => (it.id === item.id ? { ...it, status, ...(hasNote ? { description: note } : {}) } : it)) }));
    if (isDemo) return;
    try { await api.patch(`/public/r/${token}/items/${item.id}/status`, hasNote ? { status, note } : { status }); }
    catch { toast.error("Couldn't save status"); }
  }, [token, isDemo]);

  const [uploading, setUploading] = useState({});
  const [failedItems, setFailedItems] = useState({});
  const clearFailed = (itemId) => setFailedItems((f) => { if (!f[itemId]) return f; const n = { ...f }; delete n[itemId]; return n; });
  const markFailed = (itemId) => setFailedItems((f) => ({ ...f, [itemId]: true }));
  // A proof photo means the item is finished — auto-mark it complete (unless it's
  // flagged as an issue, N/A, or already done). The worker can tap the item to undo.
  const markDoneOnProof = (itemId) => {
    const cur = (data?.items || []).find((it) => it.id === itemId);
    if (cur && ["issue", "na", "done", "sub_done"].includes(cur.status)) return;
    setData((d) => ({ ...d, items: d.items.map((it) => (it.id === itemId ? { ...it, status: "done" } : it)) }));
    if (!isDemo) api.patch(`/public/r/${token}/items/${itemId}/status`, { status: "done" }).catch(() => {});
  };
  const uploadProofInstant = async (itemId, files) => {
    if (isDemo) {
      const url = URL.createObjectURL(files[0]);
      setData((d) => ({ ...d, items: d.items.map((it) => (it.id === itemId ? { ...it, photos: [...it.photos, { id: `demo-${Date.now()}`, kind: "proof", url, thumb_url: url, caption: "" }] } : it)) }));
      markDoneOnProof(itemId);
      return;
    }
    setUploading((u) => ({ ...u, [itemId]: true }));
    clearFailed(itemId);
    try {
      await uploadPhotos(files, { endpoint: `/public/r/${token}/items/${itemId}/proof`, fields: { caption: "" }, onEach: (p) => {
        setData((d) => ({ ...d, items: d.items.map((it) => (it.id === itemId ? { ...it, photos: [...it.photos, p] } : it)) }));
      } });
      markDoneOnProof(itemId);
    } catch {
      markFailed(itemId);
      toast.error("Photo didn't upload — check your signal and tap the photo button to try again");
    } finally {
      setUploading((u) => { const n = { ...u }; delete n[itemId]; return n; });
    }
  };
  // Snap a proof → upload instantly (no extra tap). If the item is flagged as an
  // issue, open the annotator first so the worker can circle/mark the problem.
  const takeProof = (itemId, files) => {
    if (!files?.length) return;
    const arr = Array.from(files);
    const item = (data?.items || []).find((it) => it.id === itemId);
    if (item && item.status === "issue") setAnnot({ files: arr, itemId });
    else uploadProofInstant(itemId, arr);
  };
  const handleProofSave = async (blob, note) => {
    const itemId = annot?.itemId;
    if (!itemId) return;
    if (isDemo) {
      const url = URL.createObjectURL(blob);
      setData((d) => ({ ...d, items: d.items.map((it) => (it.id === itemId ? { ...it, photos: [...it.photos, { id: `demo-${Date.now()}`, kind: "proof", url, thumb_url: url, caption: note || "" }] } : it)) }));
      return;
    }
    setUploading((u) => ({ ...u, [itemId]: true }));
    clearFailed(itemId);
    try {
      await uploadPhotos([blob], { endpoint: `/public/r/${token}/items/${itemId}/proof`, fields: { caption: note || "" }, onEach: (p) => {
        setData((d) => ({ ...d, items: d.items.map((it) => (it.id === itemId ? { ...it, photos: [...it.photos, p] } : it)) }));
      } });
    } catch {
      markFailed(itemId);
      toast.error("Photo didn't upload — check your signal and tap the photo button to try again");
    } finally {
      setUploading((u) => { const n = { ...u }; delete n[itemId]; return n; });
    }
  };

  const deleteProof = useCallback(async (itemId, photoId) => {
    const cur = (data?.items || []).find((it) => it.id === itemId);
    const proofLeft = cur ? (cur.photos || []).filter((p) => p.kind === "proof" && p.id !== photoId).length : 0;
    const reverted = !!cur && proofLeft === 0 && (cur.status === "done" || cur.status === "sub_done");
    setData((d) => ({ ...d, items: d.items.map((it) => (it.id === itemId ? { ...it, photos: (it.photos || []).filter((p) => p.id !== photoId), ...(reverted ? { status: "pending" } : {}) } : it)) }));
    if (isDemo) return;
    try { await api.delete(`/public/r/${token}/photos/${photoId}`); }
    catch { toast.error("Couldn't remove photo — try again"); }
    if (reverted) api.patch(`/public/r/${token}/items/${itemId}/status`, { status: "pending" }).catch(() => {});
  }, [token, isDemo, data]);

  const submit = async () => {
    const its = data?.items || [];
    const anyComplete = its.some((it) => (it.photos || []).some((p) => p.kind === "proof") || ["done", "issue", "na", "sub_done"].includes(it.status));
    if (!anyComplete) {
      toast.error(t("nothingToSend"));
      setSendOpen(false);
      return;
    }
    setSending(true);
    try {
      if (!isDemo) {
        await api.post(`/public/r/${token}/send-back`, { name: actorName.trim(), message: message.trim() });
        try { if (actorName.trim()) localStorage.setItem(NAME_KEY, actorName.trim()); } catch { /* ignore */ }
      }
      setData((d) => ({ ...d, report: { ...d.report, status: "submitted" } }));
      setSendOpen(false);
      setCelebrate(true);
      // Gentle install nudge AFTER they've sent proof — never a first-open interrupt.
      try {
        const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone;
        if (!standalone && !inAppBrowser) setTimeout(() => window.dispatchEvent(new Event("sp:install-prompt")), 1800);
      } catch { /* ignore */ }
    } catch { toast.error("Could not send — try again"); }
    finally { setSending(false); }
  };

  // Recipient → Creator loop: a worker/crew boss who just sent proof can spin up
  // their OWN free account in one tap and start sending lists to their own crew.
  // This is the product-led growth surface — the recipient becomes a creator.
  const startOwnList = async () => {
    if (startingOwn) return;
    setStartingOwn(true);
    try { analytics.trackEvent("recipient_create_cta", { token }); } catch { /* ignore */ }
    try {
      await tryFree();
      try { sessionStorage.setItem("sp_welcome", "1"); } catch { /* ignore */ }
      navigate("/dashboard");
    } catch {
      toast.error("Couldn't start your account — try again");
      setStartingOwn(false);
    }
  };

  // One-tap "save to my free account" — mints an anonymous sandbox account
  // (no signup form) and attaches this shared list to it, then drops the worker
  // into their dashboard with the list already saved. Mirrors the web "Try it free" flow.
  const saveMyList = async () => {
    if (savingList) return;
    setSavingList(true);
    try {
      await tryFree();
      const r = await api.post("/shared/attach", { token }).catch(() => null);
      if (!r) toast.message("Saved your account — syncing this list…");
      navigate("/dashboard?saved=1");
    } catch {
      toast.error("Couldn't save your list — try again");
      setSavingList(false);
    }
  };

  if (loading) return <div className="flex min-h-[100dvh] items-center justify-center bg-[#0a0a0a]"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;
  if (notFound) return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-[#0a0a0a] px-6 text-center">
      <ShieldCheck className="h-10 w-10 text-neutral-600" />
      <h1 className="mt-3 font-heading text-xl font-bold text-white">Link not found</h1>
      <p className="mt-1 text-sm text-neutral-400">This punch list link is invalid or has been removed.</p>
    </div>
  );

  const { job, report, items } = data;
  const brandName = data.brand?.company_name?.trim() || "The sender";
  const total = items.length;
  const doneCount = items.filter((it) => (it.photos || []).some((p) => p.kind === "proof") || ["done", "issue", "na", "sub_done"].includes(it.status)).length;
  const canSend = doneCount > 0;
  const failedCount = Object.keys(failedItems).length;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;
  const isChild = !!report.parent_report_id;
  const t = makeT(lang);
  const doneLabel = t("doneCount").replace("{done}", doneCount).replace("{total}", total);

  return (
    <div className="min-h-[100dvh] overflow-x-hidden bg-[#0a0a0a] text-white" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 128px)" }}>
      {inAppBrowser && !isAuthed && !iabDismissed && (
        <div className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-3" style={{ paddingTop: "calc(env(safe-area-inset-top) + 12px)" }} data-testid="inapp-browser-banner">
          <div className="mx-auto flex max-w-2xl items-start gap-3">
            <ExternalLink className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-amber-200">You're in an in-app browser</p>
              <p className="mt-0.5 text-[12px] leading-snug text-amber-100/80">Open this in your normal browser to save it to your Send-Proof account.</p>
              <button onClick={openInBrowser} data-testid="open-in-browser-btn" className="mt-2 inline-flex items-center gap-1 rounded-lg bg-amber-400 px-3 py-1.5 text-[13px] font-bold text-amber-950 active:scale-95">
                Open in browser <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            <button onClick={() => setIabDismissed(true)} data-testid="inapp-banner-dismiss" aria-label="Dismiss" className="shrink-0 text-amber-300/70"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-neutral-800 bg-[#0a0a0a]/95 backdrop-blur-md" style={{ paddingTop: "calc(env(safe-area-inset-top) + 24px)" }}>
        <div className="mx-auto max-w-2xl px-4 pb-4" data-testid="public-report-header">
          {isAuthed && (
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="public-back"
              aria-label="Back to home"
              className="mb-2 -ml-1 flex h-9 w-9 items-center justify-center rounded-full text-neutral-300 transition-transform active:scale-90"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          {isDemo && (
            <button onClick={() => navigate("/")} data-testid="demo-exit" className="mb-3 -ml-1 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-[13px] font-semibold text-neutral-100 transition-transform active:scale-95">
              <ArrowLeft className="h-4 w-4" /> Back to Send-Proof
            </button>
          )}
          <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-emerald-400">{t("punchList")}</div>
          {isDemo && (
            <div data-testid="demo-badge" className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-300">
              <Sparkles className="h-3 w-3" /> Interactive demo · tap around — nothing you do is saved
            </div>
          )}
          <h1 className="mt-1 font-heading text-[26px] font-bold leading-tight tracking-tight sm:text-[30px]">{report.title || job.name}</h1>
          <div className="mt-1 flex items-center gap-1.5 truncate text-sm text-neutral-400">
            <span className="truncate">{job.name}</span>
            {job.location && <><span>·</span><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{job.location}</span></>}
          </div>
          {isChild && report.dispatched_trade && (
            <div className="mt-2 inline-flex items-center gap-1 rounded-md bg-emerald-500/20 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-emerald-400">
              <HardHat className="h-3 w-3" /> {report.dispatched_trade}
            </div>
          )}
          <div className="mt-3 flex items-center gap-2">
            <Globe className="h-4 w-4 shrink-0 text-neutral-400" />
            <select
              value={lang}
              onChange={(e) => doTranslate(e.target.value)}
              data-testid="translate-select"
              className="rounded-lg border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm text-neutral-200 focus:border-emerald-500 focus:outline-none"
            >
              <option value="">Original (English)</option>
              <option value="es">Español</option>
              <option value="pt">Português</option>
              <option value="ht">Kreyòl Ayisyen</option>
              <option value="fr">Français</option>
              <option value="vi">Tiếng Việt</option>
              <option value="zh">中文 (简体)</option>
              <option value="ru">Русский</option>
              <option value="ko">한국어</option>
              <option value="pl">Polski</option>
            </select>
            {translating && <Loader2 className="h-4 w-4 animate-spin text-emerald-400" data-testid="translate-spinner" />}
            {lang && !translating && (
              <button onClick={() => doTranslate("")} data-testid="translate-reset" className="text-xs font-semibold text-emerald-400">{t("showOriginal")}</button>
            )}
          </div>
          <div className="mt-3">
            <div className="mb-1.5 flex items-center justify-between text-[13px]">
              <span className="text-neutral-400">{t("progress")}</span>
              <span className="font-bold text-emerald-400" data-testid="public-progress-label">{doneCount}/{total} · {pct}%</span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-neutral-800">
              <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500" style={{ width: `${pct}%` }} data-testid="public-progress-bar" />
            </div>
          </div>
        </div>
      </header>

      {/* Proof instruction banner — slim single line */}
      {!readOnly && !bannerDismissed && (
        <div className="mx-auto mt-3 max-w-2xl px-4">
          <div className="flex items-center gap-2.5 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.08] px-3.5 py-2.5" data-testid="proof-instruction-banner">
            <Camera className="h-4 w-4 shrink-0 text-emerald-400" strokeWidth={2.5} />
            <p className="min-w-0 flex-1 text-[13px] font-medium leading-snug text-emerald-100/90">{t("bannerShort")}</p>
            <button onClick={dismissBanner} data-testid="dismiss-banner-btn" className="shrink-0 rounded-full p-0.5 text-emerald-400/70 active:text-emerald-300"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      {/* Items */}
      <div className="mx-auto max-w-2xl space-y-5 px-4 pt-4 pb-64">
        {items.map((it, i) => (
          <ItemCard
            key={it.id}
            index={i}
            item={it}
            readOnly={readOnly}
            pending={pending[it.id]}
            onToggle={toggleComplete}
            onStatus={setStatus}
            onTakeProof={takeProof}
            onDeleteProof={deleteProof}
            proofUploading={!!uploading[it.id]}
            uploadFailed={!!failedItems[it.id]}
            onOpenPhoto={setLightbox}
            tr={tmap[it.id]}
            t={t}
          />
        ))}
      </div>

      {/* Sticky footer — Send (centered) with Save-your-list nudge directly below */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-neutral-800 bg-[#0a0a0a]/95 backdrop-blur-lg" style={{ paddingTop: "14px", paddingBottom: "calc(env(safe-area-inset-bottom) + 14px)" }}>
        <div className="mx-auto flex max-w-2xl flex-col items-center gap-3 px-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1.5 text-[15px] font-bold">
              {alreadySent && <Check className="h-4 w-4 shrink-0 text-emerald-400" data-testid="sent-done" />}
              {alreadySent ? t("sentBack") : doneLabel}
            </div>
            <div className="text-[12px] text-neutral-500">{alreadySent ? doneLabel : t("sendHint")}</div>
          </div>
          <button
            data-testid="send-open-btn"
            onClick={() => { if (!canSend) { toast.error(t("nothingToSend")); return; } setSendOpen(true); }}
            className="flex w-full max-w-xs items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 py-3.5 text-[15px] font-bold text-black shadow-lg shadow-emerald-500/30 active:scale-95"
          >
            <Send className="h-5 w-5" strokeWidth={2.5} />
            {alreadySent ? t("sendAgain") : t("send")}
          </button>

          {!readOnly && !isDemo && !localStorage.getItem("sp_token") && (
            <button
              data-testid="save-list-nudge"
              onClick={saveMyList}
              disabled={savingList}
              className="flex w-full max-w-xs items-center justify-center gap-2 rounded-full border border-sky-500/40 bg-sky-500/10 px-4 py-2.5 text-[14px] font-semibold text-sky-200 transition-transform active:scale-[0.98] disabled:opacity-60"
            >
              {savingList ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {savingList ? "Saving…" : "Save your list — free"}
            </button>
          )}
        </div>
      </div>

      {/* Send sheet */}
      {sendOpen && (
        <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center" onClick={() => setSendOpen(false)} data-testid="send-modal">
          <div className="w-full rounded-t-3xl border border-neutral-800 bg-[#141414] p-6 sm:max-w-sm sm:rounded-3xl" onClick={(e) => e.stopPropagation()} style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)" }}>
            <div className="flex items-start justify-between">
              <h3 className="font-heading text-xl font-bold">{t("sendProofBack")}</h3>
              <button onClick={() => setSendOpen(false)} className="text-neutral-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-1 text-sm text-neutral-400">{doneCount} of {total} items complete.</p>
            {failedCount > 0 && (
              <div className="mt-3 flex items-start gap-2 rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-2.5" data-testid="send-upload-warning">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" />
                <p className="text-[13px] font-medium text-rose-200">{t("uploadFailedWarn").replace("{n}", failedCount)}</p>
              </div>
            )}
            <div className="mt-4 space-y-3">
              <input
                data-testid="send-name-input"
                autoComplete="name"
                value={actorName}
                onChange={(e) => setActorName(e.target.value)}
                placeholder="Your name (optional)"
                className="h-12 w-full rounded-xl border border-neutral-700 bg-neutral-900 px-4 text-[15px] text-white placeholder:text-neutral-500 focus:border-emerald-500 focus:outline-none"
              />
              <textarea
                data-testid="send-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Anything the sender should know? e.g. 'Ordered replacement, ETA Friday'"
                rows={3}
                className="w-full resize-none rounded-xl border border-neutral-700 bg-neutral-900 px-4 py-3 text-[15px] text-white placeholder:text-neutral-500 focus:border-emerald-500 focus:outline-none"
              />
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setSendOpen(false)} className="h-12 flex-1 rounded-xl border border-neutral-700 font-semibold text-neutral-300">Cancel</button>
              <button data-testid="send-confirm-btn" onClick={submit} disabled={sending} className="flex h-12 flex-1 items-center justify-center gap-1 rounded-xl bg-emerald-500 font-bold text-black active:scale-95 disabled:opacity-60">
                {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-4 w-4" /> Send</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Celebration */}
      {celebrate && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#0a0a0a] px-8 text-center" data-testid="celebration">
          <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-emerald-500/20">
            <span className="absolute -inset-2 animate-ping rounded-full border-2 border-emerald-500/30" />
            <PartyPopper className="h-12 w-12 text-emerald-400" />
          </div>
          <h2 className="mt-6 text-2xl font-bold">Sent!</h2>
          {!isDemo && (
            <div className="mt-3 flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-4 py-1.5 text-sm font-semibold text-emerald-400" data-testid="sent-receipt">
              <Check className="h-4 w-4" strokeWidth={3} /> {brandName} has been notified
            </div>
          )}
          <p className="mt-3 max-w-xs text-sm text-neutral-400">
            {isDemo
              ? "In the real Send-Proof, the sender gets an instant notification the moment you send proof back."
              : `${actorName.trim() ? `Thanks ${actorName.trim()} — your` : "Your"} proof is in. You can safely close this page.`}
          </p>
          <div className="mt-4 rounded-full bg-emerald-500/15 px-4 py-1.5 text-sm font-semibold text-emerald-400">{doneCount} of {total} items complete</div>
          {!isAuthed ? (
            <div className="mt-8 w-full max-w-xs">
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.07] p-4 text-left" data-testid="recipient-create-card">
                <p className="text-[15px] font-bold text-white">Run your own crew?</p>
                <p className="mt-1 text-[13px] leading-snug text-neutral-400">Send lists like this and get photo proof back — before anyone leaves the job. Free to start, no credit card.</p>
                <button
                  onClick={startOwnList}
                  disabled={startingOwn}
                  data-testid="recipient-create-btn"
                  className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 text-[15px] font-bold text-black transition-transform active:scale-95 disabled:opacity-60"
                >
                  {startingOwn ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" strokeWidth={3} />}
                  {startingOwn ? "Setting up…" : "Start my first list — free"}
                </button>
              </div>
              <button onClick={() => window.dispatchEvent(new Event("sp:install-prompt"))} data-testid="celebrate-install-btn" className="mt-4 flex w-full items-center justify-center gap-2 text-[14px] font-semibold text-neutral-300 active:scale-95"><Download className="h-4 w-4" /> Add Send-Proof to your phone</button>
              <button onClick={() => { setCelebrate(false); setShowList(true); }} className="mt-3 w-full text-sm font-medium text-neutral-500 underline underline-offset-4">Back to punch list</button>
            </div>
          ) : (
            <>
              <button onClick={() => window.dispatchEvent(new Event("sp:install-prompt"))} data-testid="celebrate-install-btn" className="mt-8 flex items-center gap-2 rounded-full bg-white px-6 py-3 text-[15px] font-bold text-black active:scale-95"><Download className="h-4 w-4" /> Add Send-Proof to your phone</button>
              <button onClick={() => { setCelebrate(false); setShowList(true); }} className="mt-4 text-sm font-medium text-neutral-400 underline underline-offset-4">Back to punch list</button>
            </>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && <PhotoLightbox data={lightbox} onClose={() => setLightbox(null)} testId="lightbox" />}

      {annot && (
        <PhotoAnnotator files={annot.files} startLocked onSaveEach={handleProofSave} onDone={() => setAnnot(null)} onCancel={() => setAnnot(null)} />
      )}

      {/* First-open "save this list to your phone" offer — no signup, once per 7 days */}
      <WorkerSaveModal enabled={!isAuthed && !isDemo && !inAppBrowser} jobName={job?.name} />

      {/* Install (Add to Home Screen) — manual only: fired from the celebration after proof is sent */}
      <InstallPrompt auto={false} />
    </div>
  );
}
